import { useCallback, useEffect, useMemo, useState } from 'react'

import { Box, Grid, Stack, Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { defaultErrorMessage } from 'content/alertText'

import { Game } from 'models/gameModels'

import { IUser, useAuth } from 'contexts/AuthProvider'
import { useGameContext } from 'contexts/GameProvider'
import { MODAL_TYPES, useModalContext } from 'contexts/ModalProvider'
import { useToastContext } from 'contexts/ToastProvider'

import { CallToAction } from 'components/CallToAction/CallToAction'
import CustomGameCard from 'components/CustomGameCard/CustomGameCard'
import EmptySubPageContent from 'components/EmptySubPageContent/EmptySubPageContent'
import SubPageHeader from 'components/SubPageHeader/SubPageHeader'

import Wind2 from '../../assets/images/Wind2.svg'
import { styles } from './PublishedGames.styles'
import FilterRowWithButton from './components/FilterRowWithButton/FilterRowWithButton'

export const TEST_ID = 'published-games-page'

const PublishedGames = () => {
	const {
		games = [],
		requestGames,
		requestGamesLoading,
		createGameLoading,
		createdGame,
		createGameSuccess,
		editGameError,
		editGameLoading,
		editGameSuccess,
		editedGame,
		requestDeleteGame,
		deleteGameError,
		deleteGameLoading,
		deleteGameSuccess,
		deletedGame,
	} = useGameContext()

	const { showAlert } = useToastContext()

	const { showModal } = useModalContext()

	const { user } = useAuth()

	const [currentFilter, setCurrentFilter] = useState('ALL GAMES')
	const [noGamesSubtitle, setNoGamesSubtitle] = useState('')

	const handleTabChange = useCallback(
		(_tabIndex: number, tabLabel: string) => {
			setCurrentFilter(tabLabel)
		},
		[]
	)

	useEffect(() => {
		if (createGameSuccess && requestGames) {
			requestGames({
				gameTypeId: '',
				gameTitle: '',
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createdGame, createGameSuccess])
	useEffect(() => {
		const gameStateStr = sessionStorage.getItem('gameState')
		if (gameStateStr) {
			showModal(MODAL_TYPES.CUSTOM_GAME_MODAL, {
				gameState: JSON.parse(gameStateStr),
			})
			sessionStorage.removeItem('gameState')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (editGameSuccess && requestGames) {
			requestGames({
				gameTypeId: '',
				gameTitle: '',
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editedGame, editGameSuccess])

	useEffect(() => {
		if (deleteGameSuccess && requestGames) {
			requestGames({
				gameTypeId: '',
				gameTitle: '',
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deletedGame, deleteGameSuccess])

	useEffect(() => {
		if (!deleteGameLoading && deleteGameSuccess && showAlert) {
			showAlert({
				severity: ToastSeverity.SUCCESS,
				objectType: 'deleteGame',
				gameName: deletedGame?.gameTitle,
			})
		}

		if (!createGameLoading && createGameSuccess && showAlert) {
			showAlert({
				severity: ToastSeverity.SUCCESS,
				objectType: 'createGame',
			})
		}

		if (!deleteGameLoading && deleteGameError && showAlert) {
			showAlert(defaultErrorMessage)
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		createGameLoading,
		createGameSuccess,
		deleteGameError,
		deleteGameLoading,
		deleteGameSuccess,
	])

	useEffect(() => {
		if (!editGameLoading && editGameSuccess && showAlert) {
			showAlert({
				severity: ToastSeverity.SUCCESS,
				objectType: 'updateGame',
				gameName: editedGame?.gameTitle,
			})
		}

		if (!editGameLoading && editGameError && showAlert) {
			showAlert(defaultErrorMessage)
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editGameError, editGameLoading, editGameSuccess])

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			if (requestGames) {
				requestGames({
					gameTypeId: '',
					gameTitle: '',
				})
			}
		}, 500)

		return () => clearTimeout(delayDebounceFn)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const openModal = useCallback(() => {
		showModal(MODAL_TYPES.CUSTOM_GAME_MODAL)
	}, [showModal])

	const filterGamesByTab = useCallback(
		(tab: string) => {
			switch (tab) {
				case 'ALL GAMES':
					return games
				case 'GAMES I CREATED':
					setNoGamesSubtitle(
						'There are no custom games I have created yet.'
					)
					return games.filter(
						(game) =>
							game.isAuthor &&
							game.endTime &&
							new Date(game.endTime) > new Date()
					)
				case "GAMES I'M INVITED TO":
					setNoGamesSubtitle(
						'There are no custom games I have been invited to yet.'
					)
					return games.filter(
						(game) =>
							game.isParticipant &&
							!game.isAuthor &&
							game.endTime &&
							new Date(game.endTime) > new Date()
					)
				case 'EXPIRED GAMES':
					// Add logic for expired games when available
					return games.filter((game) => {
						if (game.endTime) {
							setNoGamesSubtitle(
								'There are no expired games I have creeated or been part of yet.'
							)
							return new Date(game.endTime) < new Date()
						}
						return false
					})
				default:
					return games
			}
		},
		[games]
	)

	// Compute filtered games based on current filter
	const filteredGames = useMemo(() => {
		if (games) {
			return filterGamesByTab(currentFilter)
		}
		return false
	}, [games, currentFilter, filterGamesByTab])

	const canCreateCustomGame = (user: IUser) => {
		// Defensive extraction of role_id from user object
		const roleId = user?.session?.idToken?.payload?.role_id
		return roleId === '2'
	}

	return (
		<Stack data-testid={TEST_ID} sx={styles.StackContainer}>
			<Box>
				<SubPageHeader
					title="Multi-Facilitator Games"
					subtitle="For large or asynchronous groups, create custom games to access submitted ideas from others playing your game"
					backgroundGradient="linear-gradient(90deg, #9DCDF2 0%, #BDDEF6 100%)"
				/>
			</Box>
			<Box sx={styles.ContentBox}>
				{requestGamesLoading ? (
					<Box sx={styles.ProgressContainer}>
						<CircularProgress
							data-testid={`${TEST_ID}-progress`}
							sx={styles.CircularProgressIcon}
						/>
					</Box>
				) : games && games.length > 0 ? (
					<Grid container spacing={4} sx={styles.GamesGrid}>
						<FilterRowWithButton
							buttonOnClick={openModal}
							disabled={!canCreateCustomGame(user)}
							onTabChange={handleTabChange}
						/>
						{filteredGames && filteredGames.length > 0 ? (
							filteredGames?.map((game: Game) => (
								<Grid
									key={game.gameId}
									item
									lg={4}
									md={4}
									sm={12}
									xs={12}
									sx={styles.GameGridItem}
								>
									<CustomGameCard
										game={game}
										handleDeleteRequest={requestDeleteGame}
										testId={`custom-game-card-${game.gameId}`}
									/>
								</Grid>
							))
						) : (
							<Box sx={styles.EmptyStateContainer}>
								<img src={Wind2 as unknown as string} />
								<Typography sx={styles.EmptyStateText}>
									{noGamesSubtitle}
								</Typography>
							</Box>
						)}
					</Grid>
				) : (
					<EmptySubPageContent
						subtitle="No custom games created yet, create a custom game to get started!"
						buttonText="Create Custom Game"
						buttonOnClick={openModal}
						disabled={!canCreateCustomGame(user)}
					/>
				)}
			</Box>
			<Box sx={styles.CallToActionBox}>
				<Divider sx={styles.Divider2} />
				<CallToAction
					data-testid={TEST_ID}
					helperText={'Want help creating a custom game?'}
				/>
			</Box>
		</Stack>
	)
}

export default PublishedGames
